import {
  Admin,
  Resource,
  ShowGuesser,
  type AuthProvider,
  CustomRoutes,
  Notification,
  type TranslationMessages,
} from 'react-admin';
import { Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';
import * as Sentry from '@sentry/react';
import { signOut } from 'firebase/auth';
import { auth } from '../core/firebaseConfig';
import LoginPage from '../pages/login/LoginPage';
import ForgotPasswordPage from '../pages/login/ForgotPasswordPage';
import { MyLayout } from '../components/layout/Layout';
import { MembersTable } from '../components/tables/membersTable';
import { MembersTableCreate } from '../components/tables/membersTableCreate';
import { LocationTable } from '../components/tables/locationsTable';
import { theme } from '../core/theme/theme';
import OrdersScreen from '../pages/orders/OrdersPage';
import OrderHistoryList from '../pages/orderHistory/OrderHistoryList';
import OrderHistoryShow from '../pages/orderHistory/OrderHistoryShow';
import dataProvider from '../core/dataProvider';
import { MembersEditTable } from '../components/tables/membersEditTable';
import LocationsCreate from '../pages/locations/locationsCreatePage';
import { LocationItemsTable } from '../components/tables/locationItemsTable';
import LocationsCreateItem from '../pages/locations/locationsCreateItemPage';
import LocationsPromotionPage from '../pages/locations/locationsPromotionPage';
import DotykackaMappingPage from '../pages/dotykacka/DotykackaMappingPage';
import DotykackaReturnPage from '../pages/dotykacka/DotykackaReturnPage';
import { OrdersMapScreen } from '../pages/map/MapPage';
import { LocationCategoryTable } from '../components/tables/locationCategoryTable';
import { MenuOrderPage } from '../components/tables/menuOrderPage';
import { MenuItemOrderPage } from '../components/tables/menuItemOrderPage';
import WoltCheckerPage from '../pages/woltChecker/WoltCheckerPage';
import QrCodesPage from '../pages/qrCodes/QrCodesPage';
import MonthlyFeeDefaultsPage from '../pages/monthlyFees/MonthlyFeeDefaultsPage';
import CouponsList from '../pages/coupons/CouponsList';
import CouponFormPage from '../pages/coupons/CouponFormPage';
import DiscountsList from '../pages/discounts/DiscountsList';
import DiscountFormPage from '../pages/discounts/DiscountFormPage';
import DiscountKindsPage from '../pages/discounts/DiscountKindsPage';
// Rejestruje obsluge alertu o wyczerpanym budzecie rabatu (useAdminEvents).
import '../pages/discounts/discountAdminEvents';
// Alert o anulowaniu zamowienia przez problem z dostawa Wolt.
import '../pages/orders/orderAdminEvents';
import VariantTemplatesPage from '../pages/variantTemplates/VariantTemplatesPage';
import raPolishMessages from '../core/i18n/raPolishMessages';
import { getInitialLanguage } from '../core/i18n';
import { hasRole, isAdminRole } from '../utils/roles';

const raMessages: Record<string, TranslationMessages> = {
  pl: raPolishMessages as unknown as TranslationMessages,
  en: englishMessages,
};

// NOTE: intentionally no `availableLocales` array here. react-admin's default
// AppBar auto-renders a <LocalesMenuButton> when getLocales() returns more than
// one locale — that button only calls setLocale (react-admin) and would leave
// the react-i18next-driven page untranslated. We drive language exclusively via
// the custom LanguageSwitcher (globe), which updates both systems.
const i18nProvider = polyglotI18nProvider(
  (locale) => raMessages[locale] ?? raMessages.pl,
  getInitialLanguage(),
  { allowMissing: true },
);
// import fakeRestProvider from 'ra-data-fakerest';
// import { fakeData } from '../mocks/index';
// const fakeProvider = fakeRestProvider(fakeData, true);

const authProvider: AuthProvider = {
  login: async (_params) => {
    localStorage.setItem('auth', 'true');
    const { token, uid } = _params || {};
    localStorage.setItem('ra_token', token);
    localStorage.setItem('ra_uid', uid || '');
    return Promise.resolve();
  },
  logout: async () => {
    // Samo czyszczenie localStorage zostawialo sesje Firebase - na wspolnym terminalu token
    // dalej dzialal. signOut konczy ja po stronie klienta.
    try {
      await signOut(auth);
    } catch (error) {
      Sentry.captureException(error);
    }
    localStorage.removeItem('auth');
    localStorage.removeItem('ra_token');
    localStorage.removeItem('ra_uid');
    localStorage.removeItem('ra_role');
    Sentry.setUser(null);
    return Promise.resolve();
  },
  checkAuth: async () => {
    // Zrodlem prawdy jest sesja Firebase, nie flaga w localStorage (ra_role to tylko
    // podpowiedz dla UI). Czekamy az Firebase odtworzy sesje po przeladowaniu strony.
    await auth.authStateReady();
    if (auth.currentUser) {
      return Promise.resolve();
    }
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    const isSilentPath = path === '' || path === 'login';
    return Promise.reject(isSilentPath ? { message: false } : undefined);
  },
  checkError: async () => Promise.resolve(),
  getPermissions: async () => Promise.resolve(),
};

const MyNotification = () => (
  <Notification anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
);

const ErrorFallback = () => {
  const { t } = useTranslation();

  return (
    <div style={{ padding: 32, textAlign: 'center' }}>
      <h2>{t('errorFallback.title')}</h2>
      <p>{t('errorFallback.description')}</p>
      <button onClick={() => window.location.reload()}>{t('errorFallback.reload')}</button>
    </div>
  );
};

function App() {
  const { t } = useTranslation();

  const isVendor = hasRole('VENDOR');
  const isRestaurantOwner = hasRole('RESTAURANT_OWNER');

  return (
    // <SupportNotificationProvider>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
      <Admin
        dataProvider={dataProvider}
        theme={theme}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        notification={MyNotification}
        loginPage={LoginPage}
        layout={MyLayout}
      >
        {(!isVendor || !isRestaurantOwner) && (
          <Resource
            options={{ label: t('resources.members') }}
            name="members"
            list={MembersTable}
            edit={MembersEditTable}
            create={MembersTableCreate}
            show={ShowGuesser}
          />
        )}
        {/*{(!isVendor || !isRestaurantOwner) && <Resource*/}
        {/*  options={{ label: t('resources.commissionManagement') }}*/}
        {/*  name="commissionManagement"*/}
        {/*  list={CommissionTable}*/}
        {/*  create={CommissionCreateDialog}*/}
        {/*  edit={EditGuesser}*/}
        {/*  show={ShowGuesser}*/}
        {/*/>}*/}
        <Resource
          options={{ label: t('resources.locationsManagement') }}
          name="locationsManagement"
          create={LocationsCreate}
          list={LocationTable}
          edit={LocationsCreate}
          show={ShowGuesser}
        />
        <Resource options={{ label: t('resources.orders') }} name="orders" list={OrdersScreen} />
        <Resource
          options={{ label: t('resources.orderHistory') }}
          name="orderHistory"
          list={OrderHistoryList}
          show={OrderHistoryShow}
        />
        <Resource
          options={{ label: t('resources.coupons') }}
          name="coupons"
          list={CouponsList}
          create={CouponFormPage}
          edit={CouponFormPage}
        />
        <Resource
          options={{ label: t('resources.discounts') }}
          name="discounts"
          list={DiscountsList}
          create={DiscountFormPage}
          edit={DiscountFormPage}
        />
        <Resource
          options={{ label: t('resources.variantTemplates') }}
          name="variantTemplates"
          list={VariantTemplatesPage}
        />
        <CustomRoutes noLayout>
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </CustomRoutes>
        <CustomRoutes>
          {/*{(!isVendor || !isRestaurantOwner) &&*/}
          {/*  <>*/}
          {/*    <Route path="/commission/:id/couriers" element={<CommissionCouriersTable />} />*/}
          {/*    <Route path="/riderManagement/:id/performance" element={<RiderPerformanceTable />} />*/}
          {/*  </>*/}
          {/*}*/}
          <Route path="/discount-types" element={<DiscountKindsPage />} />
          <Route path="/qr-codes" element={<QrCodesPage />} />
          <Route path="/locationsManagement/:id/items" element={<LocationItemsTable />} />
          <Route path="/locationsManagement/:id/items/create" element={<LocationsCreateItem />} />
          <Route path="/locationsManagement/:id/items/:itemId" element={<LocationsCreateItem />} />
          <Route path="/locationsManagement/:id/promotion" element={<LocationsPromotionPage />} />
          <Route path="/locationsManagement/promotion" element={<LocationsPromotionPage />} />
          <Route path="/locationsManagement/:id/dotykacka" element={<DotykackaMappingPage />} />
          <Route path="/dotykacka-return" element={<DotykackaReturnPage />} />
          <Route path="/locationManagement/:id/categories" element={<LocationCategoryTable />} />
          <Route path="/locationManagement/:id/menu-order" element={<MenuOrderPage />} />
          <Route
            path="/locationManagement/:id/menu-order/:categoryId"
            element={<MenuItemOrderPage />}
          />
          {/*{(!isVendor || !isRestaurantOwner) && <Route path="/support" element={<SupportScreen />} />}*/}
          <Route path="/orders/showMap" element={<OrdersMapScreen />} />
          {(!isVendor || !isRestaurantOwner) && (
            <Route path="/wolt-checker" element={<WoltCheckerPage />} />
          )}
          {isAdminRole() && <Route path="/monthly-fees" element={<MonthlyFeeDefaultsPage />} />}
        </CustomRoutes>
      </Admin>
    </Sentry.ErrorBoundary>
    // </SupportNotificationProvider>
  );
}

export default App;
